// src/components/report/manifestTracking/CheckDetailCard.js
import React, { useState } from 'react';
import {
    Box, Chip, Dialog, Grid, IconButton, Paper, Stack, Typography,
} from '@mui/material';
import {
    Close as CloseIcon, PhotoCamera as PhotoCameraIcon, ZoomIn as ZoomInIcon,
} from '@mui/icons-material';
import { formatDateTime, formatNumber } from '../../../utils/formatters';
import { CHECKS_IMAGE_BASE } from '../../../utils/manifestTrackingReportConfig';

const Stat = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="text.secondary" display="block">
            {label}
        </Typography>
        <Typography variant="body2" fontWeight={800}>{value}</Typography>
    </Box>
);

const CheckDetailCard = ({ title, data, color }) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    const registered = Boolean(data?.id);
    const imageUrl = data?.foto ? `${CHECKS_IMAGE_BASE}${data.foto}` : null;

    return (
        <>
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', height: '100%' }}>
                <Box
                    sx={{
                        bgcolor: registered ? color : 'grey.400',
                        color: '#fff',
                        p: 2,
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight={900}>{title}</Typography>
                        <Chip
                            size="small"
                            label={registered ? 'REGISTRADO' : 'NO REGISTRADO'}
                            sx={{
                                bgcolor: registered ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                                color: '#fff',
                                fontWeight: 900,
                            }}
                        />
                    </Stack>
                    <Typography variant="h5" fontWeight={900} sx={{ mt: 0.5 }}>
                        {registered ? formatDateTime(data.fecha) : '—'}
                    </Typography>
                </Box>

                {registered ? (
                    <Box sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Stat
                                    label="Kilometraje"
                                    value={data.km != null ? `${formatNumber(data.km)} km` : '—'}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Stat
                                    label="Combustible"
                                    value={data.combustible != null ? `${data.combustible}%` : '—'}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stat
                                    label="Responsable"
                                    value={data.nombre || `Empleado #${data.emp_id}`}
                                />
                            </Grid>
                        </Grid>

                        {data.observacion && (
                            <Box
                                sx={{
                                    mt: 2, p: 1.5, borderRadius: 2,
                                    bgcolor: 'action.hover',
                                    borderLeft: `3px solid ${color}`,
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                    Observación
                                </Typography>
                                <Typography variant="body2">{data.observacion}</Typography>
                            </Box>
                        )}

                        {imageUrl ? (
                            <Box
                                sx={{ mt: 2, position: 'relative', borderRadius: 2, overflow: 'hidden' }}
                            >
                                <Box
                                    component="img"
                                    src={imageUrl}
                                    alt={`Check ${title}`}
                                    onClick={() => setPreviewOpen(true)}
                                    sx={{
                                        width: '100%',
                                        maxHeight: 280,
                                        objectFit: 'cover',
                                        cursor: 'zoom-in',
                                        display: 'block',
                                        bgcolor: 'grey.100',
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => setPreviewOpen(true)}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 8,
                                        right: 8,
                                        bgcolor: 'rgba(0,0,0,0.6)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                                    }}
                                >
                                    <ZoomInIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ) : (
                            <Stack
                                alignItems="center"
                                spacing={0.5}
                                sx={{ mt: 2, py: 3, bgcolor: 'action.hover', borderRadius: 2 }}
                            >
                                <PhotoCameraIcon color="disabled" />
                                <Typography variant="caption" color="text.secondary">
                                    Sin foto adjunta
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Este check aún no ha sido registrado.
                        </Typography>
                    </Box>
                )}
            </Paper>

            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <Box sx={{ position: 'relative', bgcolor: 'black' }}>
                    <IconButton
                        onClick={() => setPreviewOpen(false)}
                        sx={{
                            position: 'absolute', top: 8, right: 8,
                            color: '#fff', bgcolor: 'rgba(0,0,0,0.5)',
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {imageUrl && (
                        <Box
                            component="img"
                            src={imageUrl}
                            alt="Vista previa"
                            sx={{ width: '100%', display: 'block', maxHeight: '85vh', objectFit: 'contain' }}
                        />
                    )}
                </Box>
            </Dialog>
        </>
    );
};

export default CheckDetailCard;